/* ============================================================
   INKHAUS THEME JS
   Cart drawer · Predictive search · Sticky ATC · Header offsets
   ============================================================ */
(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function money(cents) {
    if (window.Inkhaus && typeof window.Inkhaus.formatMoney === 'function') {
      return window.Inkhaus.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  /* ── Cart API ─────────────────────────────────────────── */
  var CartAPI = {
    get: function () {
      return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); });
    },
    change: function (key, qty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (r) { return r.json(); });
    },
    add: function (id, qty, properties) {
      var item = { id: id, quantity: qty || 1 };
      if (properties) item.properties = properties;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [item] })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Error'); });
        return r.json();
      });
    }
  };

  /* ── Cart Count Badge ─────────────────────────────────── */
  function updateCartBadge(count) {
    var badge = document.getElementById('ikh-hdr-cart-count');
    var countEl = document.getElementById('ikh-cd-count');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
    if (countEl) {
      countEl.textContent = count + ' item' + (count !== 1 ? 's' : '');
    }
  }

  /* ── Cart Drawer ──────────────────────────────────────── */
  var CartDrawer = {
    el: null,
    overlay: null,
    body: null,
    footer: null,
    isOpen: false,

    init: function () {
      var self = this;
      this.el      = document.getElementById('ikh-cart-drawer');
      this.overlay = document.getElementById('ikh-overlay');
      this.body    = document.getElementById('ikh-cd-body');
      this.footer  = document.getElementById('ikh-cd-footer');
      if (!this.el) return;

      /* Close button */
      var closeBtn = document.getElementById('ikh-cart-close');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

      /* Overlay click */
      if (this.overlay) this.overlay.addEventListener('click', function () { self.close(); });

      /* ESC key */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });

      /* Cart trigger buttons */
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-cart-trigger]');
        if (!trigger) return;
        e.preventDefault();
        if (self.isOpen) { self.close(); } else { self.open(); }
      });

      /* Init badge */
      CartAPI.get().then(function (cart) {
        updateCartBadge(cart.item_count);
        if (self.body) self._renderBody(cart);
      }).catch(function () {});
    },

    open: function () {
      if (this.isOpen) return;
      this.isOpen = true;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var stickyBar = document.getElementById('ikh2-bar');
      if (stickyBar) stickyBar.setAttribute('aria-hidden', 'true');
      this._load();
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      var stickyBar = document.getElementById('ikh2-bar');
      if (stickyBar) stickyBar.removeAttribute('aria-hidden');
    },

    _load: function () {
      var self = this;
      if (this.body) this.body.innerHTML = '<div class="ikh-cd__loading"><div class="ikh-cd__spinner"></div></div>';
      if (this.footer) this.footer.hidden = true;
      CartAPI.get().then(function (cart) {
        self._renderBody(cart);
        updateCartBadge(cart.item_count);
      }).catch(function () {
        if (self.body) self.body.innerHTML = '<p class="ikh-cd__err">Could not load cart - please refresh.</p>';
      });
    },

    _renderBody: function (cart) {
      if (!this.body) return;
      if (!cart.items || cart.items.length === 0) {
        this.body.innerHTML = this._emptyHTML();
        if (this.footer) this.footer.hidden = true;
        return;
      }
      var html = '';
      cart.items.forEach(function (item) {
        html += CartDrawer._itemHTML(item);
      });
      this.body.innerHTML = html;
      if (this.footer) {
        this.footer.innerHTML = CartDrawer._footerHTML(cart);
        this.footer.hidden = false;
      }
      this._bindEvents();
    },

    _itemHTML: function (item) {
      var imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/(\.\w{3,4})(\?.*)?$/, '_120x120$1')
        : (item.image ? item.image.replace(/(\.\w+)$/, '_120x120$1') : '');
      var img = imgSrc
        ? '<img class="ikh-cd-item__img" src="' + imgSrc + '" alt="' + (item.product_title || '').replace(/"/g, '&quot;') + '" width="80" height="80" loading="lazy">'
        : '<div class="ikh-cd-item__img ikh-cd-item__img--placeholder"></div>';
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<span class="ikh-cd-item__variant">' + item.variant_title + '</span>'
        : '';
      var unitPrice = money(item.final_line_price / item.quantity);
      return (
        '<div class="ikh-cd-item" data-key="' + item.key + '">' +
          '<a href="' + item.url + '" class="ikh-cd-item__img-link">' + img + '</a>' +
          '<div class="ikh-cd-item__body">' +
            '<a href="' + item.url + '" class="ikh-cd-item__title">' + item.product_title + '</a>' +
            variant +
            '<div class="ikh-cd-item__row">' +
              '<div class="ikh-cd-item__stepper">' +
                '<button class="ikh-cd-item__step" data-action="dec" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Decrease">−</button>' +
                '<span class="ikh-cd-item__qty">' + item.quantity + '</span>' +
                '<button class="ikh-cd-item__step" data-action="inc" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Increase">+</button>' +
              '</div>' +
              '<div class="ikh-cd-item__prices">' +
                '<span class="ikh-cd-item__price">' + money(item.final_line_price) + '</span>' +
                (item.quantity > 1 ? '<span class="ikh-cd-item__unit">' + unitPrice + ' ea</span>' : '') +
              '</div>' +
            '</div>' +
          '</div>' +
          '<button class="ikh-cd-item__remove" data-key="' + item.key + '" aria-label="Remove ' + (item.product_title || '').replace(/"/g, '&quot;') + '">' +
            '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>'
      );
    },

    _footerHTML: function (cart) {
      var freeShipping = cart.total_price >= 5000;
      var remaining = 5000 - cart.total_price;
      var shippingNote = freeShipping
        ? '<span class="ikh-cd-footer__ship-ok">✓ Free shipping applied</span>'
        : '<span class="ikh-cd-footer__ship-note">Add ' + money(remaining) + ' more for free shipping</span>';
      return (
        '<div class="ikh-cd-footer__subtotal">' +
          '<span class="ikh-cd-footer__label">Subtotal</span>' +
          '<span class="ikh-cd-footer__total">' + money(cart.total_price) + '</span>' +
        '</div>' +
        '<div class="ikh-cd-footer__ship">' + shippingNote + '</div>' +
        '<a href="/checkout" class="ikh-cd-footer__checkout">Checkout →</a>' +
        '<a href="/cart" class="ikh-cd-footer__view-cart">View full cart</a>'
      );
    },

    _emptyHTML: function () {
      return (
        '<div class="ikh-cd__empty">' +
          '<svg class="ikh-cd__empty-icon" viewBox="0 0 48 48" fill="none">' +
            '<path d="M6 6h3.5l1.5 4m0 0 4 16h18l4-16H11z" stroke="#e2e0dc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<circle cx="18" cy="38" r="2.5" stroke="#e2e0dc" stroke-width="2"/>' +
            '<circle cx="32" cy="38" r="2.5" stroke="#e2e0dc" stroke-width="2"/>' +
          '</svg>' +
          '<p class="ikh-cd__empty-title">Your cart is empty</p>' +
          '<p class="ikh-cd__empty-sub">Add a tee to get started</p>' +
          '<a href="/collections/all" class="ikh-cd__empty-btn">Shop all tees</a>' +
        '</div>'
      );
    },

    _bindEvents: function () {
      var self = this;
      qsa('.ikh-cd-item__step', this.body).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.dataset.key;
          var qty = parseInt(btn.dataset.qty);
          var newQty = btn.dataset.action === 'inc' ? qty + 1 : Math.max(0, qty - 1);

          /* Optimistic UI */
          var item = btn.closest('.ikh-cd-item');
          if (item) {
            var qtyEl = qs('.ikh-cd-item__qty', item);
            if (qtyEl) qtyEl.textContent = newQty;
            if (newQty === 0) { item.style.opacity = '0.4'; item.style.pointerEvents = 'none'; }
            qsa('.ikh-cd-item__step', item).forEach(function (b) { b.dataset.qty = newQty; });
          }

          CartAPI.change(key, newQty).then(function (cart) {
            self._renderBody(cart);
            updateCartBadge(cart.item_count);
          });
        });
      });

      qsa('.ikh-cd-item__remove', this.body).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.dataset.key;
          var item = btn.closest('.ikh-cd-item');
          if (item) { item.style.opacity = '0.4'; item.style.pointerEvents = 'none'; }
          CartAPI.change(key, 0).then(function (cart) {
            self._renderBody(cart);
            updateCartBadge(cart.item_count);
          });
        });
      });
    }
  };

  /* ── Add To Cart (intercept form submits on PDP) ──────── */
  var AddToCart = {
    _spinner: '<svg class="ikh-btn-spin" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2" stroke-dasharray="32" stroke-dashoffset="12" stroke-linecap="round"/></svg>',

    _setLoading: function (btn, origHTML) {
      btn.disabled = true;
      btn.dataset.origHtml = origHTML;
      btn.classList.add('ikh-btn--loading');
      btn.innerHTML = this._spinner;
    },

    _setDone: function (btn) {
      btn.classList.remove('ikh-btn--loading');
      btn.classList.add('ikh-btn--done');
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 7l4 4 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    },

    _resetBtn: function (btn) {
      btn.disabled = false;
      btn.classList.remove('ikh-btn--loading', 'ikh-btn--done');
      btn.innerHTML = btn.dataset.origHtml || 'Add to Cart';
    },

    init: function () {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[action*="/cart/add"], #ikh-pdp-form');
        if (!form) return;
        e.preventDefault();

        var btn = qs('button[type="submit"]', form) || qs('.ikh-pdp__atc', form);
        var origHTML = btn ? btn.innerHTML : '';
        if (btn) self._setLoading(btn, origHTML);

        var varId = (qs('[name="id"]', form) || {}).value;
        var qty   = parseInt((qs('[name="quantity"]', form) || {}).value) || 1;

        CartAPI.add(varId, qty).then(function () {
          if (btn) self._setDone(btn);
          CartDrawer.open();
          CartAPI.get().then(function (cart) { updateCartBadge(cart.item_count); });
          setTimeout(function () {
            if (btn) self._resetBtn(btn);
          }, 2000);
        }).catch(function (err) {
          if (btn) {
            self._resetBtn(btn);
            btn.textContent = err.message || 'Could not add to cart';
            setTimeout(function () { self._resetBtn(btn); }, 2500);
          }
        });
      });
    }
  };

  /* ── Predictive Search ────────────────────────────────── */
  var Search = {
    overlay: null,
    input: null,
    results: null,
    spinner: null,
    closeBtn: null,
    isOpen: false,
    _xhr: null,

    init: function () {
      var self = this;
      this.overlay  = document.getElementById('ikh-search-overlay');
      this.input    = document.getElementById('ikh-search-input');
      this.results  = document.getElementById('ikh-search-results');
      this.spinner  = document.getElementById('ikh-search-spinner');
      this.closeBtn = document.getElementById('ikh-search-close');
      if (!this.overlay) return;

      /* Trigger */
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-search-trigger]')) {
          e.preventDefault();
          self.open();
        }
      });

      /* Close button */
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function () { self.close(); });
      }

      /* Click outside */
      this.overlay.addEventListener('click', function (e) {
        if (e.target === self.overlay) self.close();
      });

      /* ESC */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });

      /* Input - real-time search */
      if (this.input) {
        this.input.addEventListener('input', debounce(function () {
          var q = self.input.value.trim();
          if (q.length < 2) {
            self._clearResults();
            return;
          }
          self._fetch(q);
        }, 280));

        /* Enter → full search page */
        this.input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            var q = self.input.value.trim();
            if (q) window.location.href = '/search?type=product&q=' + encodeURIComponent(q);
          }
        });
      }
    },

    open: function () {
      if (this.isOpen) { this.close(); return; }
      this.isOpen = true;
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (this.input) setTimeout(function () { Search.input.focus(); }, 80);
    },

    close: function () {
      this.isOpen = false;
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._clearResults();
      if (this.input) this.input.value = '';
    },

    _clearResults: function () {
      if (this.results) this.results.innerHTML = '';
      if (this.results) this.results.hidden = true;
    },

    _fetch: function (q) {
      var self = this;
      if (this.spinner) this.spinner.classList.add('is-active');

      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor';

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (self.spinner) self.spinner.classList.remove('is-active');
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          self._renderResults(products, q);
        })
        .catch(function () {
          if (self.spinner) self.spinner.classList.remove('is-active');
        });
    },

    _renderResults: function (products, q) {
      if (!this.results) return;
      if (!products.length) {
        this.results.innerHTML =
          '<div class="ikh-so-empty">No results for <strong>"' + q + '"</strong> - <a href="/search?type=product&q=' + encodeURIComponent(q) + '">browse all</a></div>';
        this.results.hidden = false;
        return;
      }
      var html = '<div class="ikh-so-results__list">';
      products.forEach(function (p) {
        /* featured_image is {url, alt} object in suggest API, or null */
        var imgUrl = p.featured_image && p.featured_image.url
          ? p.featured_image.url
          : (typeof p.featured_image === 'string' ? p.featured_image : null);
        /* Resize via Shopify CDN: append _56x56 before query string */
        if (imgUrl) imgUrl = imgUrl.replace(/(\.\w{3,4})(\?.*)?$/, '_120x120$1');
        var img = imgUrl
          ? '<img class="ikh-so-result__img" src="' + imgUrl + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" width="56" height="56" loading="lazy">'
          : '<div class="ikh-so-result__img ikh-so-result__img--ph"></div>';
        /* price is a decimal string like "34.00" - multiply by 100 for money() */
        var priceVal = p.price ? parseFloat(p.price) * 100 : 0;
        var price = priceVal ? '<span class="ikh-so-result__price">' + money(priceVal) + '</span>' : '';
        html += (
          '<a class="ikh-so-result" href="' + p.url + '">' +
            img +
            '<div class="ikh-so-result__info">' +
              '<span class="ikh-so-result__title">' + (p.title || '') + '</span>' +
              price +
            '</div>' +
          '</a>'
        );
      });
      html += '</div>';
      html += '<a class="ikh-so-results__all" href="/search?type=product&q=' + encodeURIComponent(q) + '">See all results for &ldquo;' + q + '&rdquo; &rarr;</a>';
      this.results.innerHTML = html;
      this.results.hidden = false;
    }
  };

  /* ── Scroll: header shadow ────────────────────────────── */
  var ScrollFX = {
    init: function () {
      var hdr = document.getElementById('ikh-hdr');
      if (!hdr) return;
      window.addEventListener('scroll', function () {
        hdr.classList.toggle('is-scrolled', window.scrollY > 8);
      }, { passive: true });
    }
  };

  /* ── Sticky ATC bar + variant sheet (product page) ─────── */
  var SATC = {
    variants: [],
    sel: [],
    optCount: 0,

    init: function () {
      var bar = document.getElementById('ikh2-bar');
      if (!bar) return;

      var vEl = document.getElementById('ikh2-variants');
      if (vEl) {
        try { this.variants = JSON.parse(vEl.textContent); } catch (e) { this.variants = []; }
      }
      if (!this.variants.length) return;
      this.optCount = this.variants[0].options.length;

      /* Start from whatever the main product form has selected */
      var vidEl = document.getElementById('ikh-variant-id');
      var current = vidEl ? this.variants.filter(function (v) { return String(v.id) === vidEl.value; })[0] : null;
      this.setSelection((current || this.variants[0]).options.slice());
      this.setPrice(current || this.variants[0]);

      /* Keep in sync with the main form */
      document.addEventListener('ikh:variant', function (e) {
        SATC.setSelection(e.detail.options.slice());
        SATC.setPrice(e.detail.variant);
      });

      /* Show bar once the main ATC button scrolls out of view */
      var mainAtc = document.getElementById('ikh-atc-btn');
      if (mainAtc && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (bar.getAttribute('aria-hidden') !== 'true') {
            bar.classList.toggle('ikh2-on', !entries[0].isIntersecting);
          }
        }, { threshold: 0 }).observe(mainAtc);
      }

      var barBtn = document.getElementById('ikh2-bar-btn');
      if (barBtn) barBtn.addEventListener('click', function () { SATC.openSheet(); });
      var xBtn = document.getElementById('ikh2-sheet-x');
      var overlay = document.getElementById('ikh2-overlay');
      if (xBtn) xBtn.addEventListener('click', function () { SATC.closeSheet(); });
      if (overlay) overlay.addEventListener('click', function () { SATC.closeSheet(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') SATC.closeSheet();
      });

      /* Option picks (dots + pills share the same data attributes) */
      var sheet = document.getElementById('ikh2-sheet');
      if (sheet) {
        sheet.addEventListener('click', function (e) {
          var pick = e.target.closest('[data-opt][data-val]');
          if (!pick) return;
          var next = SATC.sel.slice();
          next[parseInt(pick.dataset.opt, 10)] = pick.dataset.val;
          SATC.setSelection(next);
        });
      }

      var atcBtn = document.getElementById('ikh2-atc');
      if (atcBtn) atcBtn.addEventListener('click', function () { SATC.addToCart(); });
    },

    setSelection: function (opts) {
      this.sel = opts;
      var sel = this.sel;
      qsa('#ikh2-sheet [data-opt][data-val]').forEach(function (el) {
        var i = parseInt(el.dataset.opt, 10);
        var active = sel[i] === el.dataset.val;
        el.classList.toggle('ikh2-on', active);
        /* Grey out values that don't exist with the other selected options */
        var test = sel.slice(); test[i] = el.dataset.val;
        var exists = SATC.variants.some(function (v) {
          return v.options.every(function (o, k) { return o === test[k]; });
        });
        el.classList.toggle('ikh2-off', !exists);
      });
      var nameEl = document.getElementById('ikh2-color-name');
      var colorDot = qs('#ikh2-sheet .ikh2-sheet__dot');
      if (nameEl && colorDot) nameEl.textContent = sel[parseInt(colorDot.dataset.opt, 10)] || '';
      /* Picks made inside the sheet must update the bar price too */
      var picked = this.findVariant();
      if (picked) this.setPrice(picked);
      this.refreshBtn();
    },

    setPrice: function (variant) {
      var el = document.getElementById('ikh2-bar-price');
      if (el && variant) el.textContent = money(variant.price);
    },

    findVariant: function () {
      var sel = this.sel;
      return this.variants.filter(function (v) {
        return v.options.every(function (o, i) { return sel[i] === o; });
      })[0] || null;
    },

    refreshBtn: function () {
      var btn = document.getElementById('ikh2-atc');
      if (!btn) return;
      var complete = this.sel.length === this.optCount && this.sel.every(function (o) { return !!o; });
      var variant = complete ? this.findVariant() : null;
      if (!complete)             { btn.disabled = true;  btn.textContent = 'Select options'; }
      else if (!variant)         { btn.disabled = true;  btn.textContent = 'Unavailable'; }
      else if (!variant.available) { btn.disabled = true; btn.textContent = 'Sold out'; }
      else                       { btn.disabled = false; btn.textContent = 'Add to Cart'; }
    },

    openSheet: function () {
      var sh = document.getElementById('ikh2-sheet');
      if (!sh) return;
      sh.classList.add('ikh2-open');
      document.body.style.overflow = 'hidden'; /* lock page scroll behind the sheet */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { sh.classList.add('ikh2-slide'); });
      });
    },

    closeSheet: function () {
      var sh = document.getElementById('ikh2-sheet');
      if (!sh || !sh.classList.contains('ikh2-open')) return;
      sh.classList.remove('ikh2-slide');
      setTimeout(function () {
        sh.classList.remove('ikh2-open');
        if (!CartDrawer.isOpen) document.body.style.overflow = '';
      }, 380);
    },

    addToCart: function () {
      var btn = document.getElementById('ikh2-atc');
      if (!btn || btn.disabled) return;
      var variant = this.findVariant();
      if (!variant) return;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      CartAPI.add(variant.id, 1)
        .then(function () {
          btn.textContent = '✓ Added';
          SATC.closeSheet(); /* close first so its overlay doesn't cover the drawer */
          setTimeout(function () {
            CartDrawer.open();
            SATC.refreshBtn();
          }, 420);
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Try again';
        });
    }
  };


  /* ── Product cards: colour dots + inline variant picker ──────────
     Shared by the collection, search and related-products cards.
     Driven entirely by data attributes rendered in Liquid:
       [data-cpg-card]            card root (data-color-opt-idx)
       [data-cpg-color-sw]        colour dot (data-color-val, data-img)
       [data-cpg-color-name]      label next to the dots
       [data-cpg-atc]             pill (data-variant-id, data-has-options)
       [data-cpg-picker]          slide-up picker inside .ikh-pc__img-wrap
       .ikh-cpg__picker-swatch    option value (data-opt-idx, data-opt-val)
       [data-cpg-picker-add]      picker CTA (data-variants, data-opt-count)
       .ikh-pc__price             price, rewritten as the variant changes
  */
  var CardPicker = {
    init: function () {
      var self = this;
      document.addEventListener('click', function (e) { self._onClick(e); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeAll();
      });
      document.addEventListener('mouseover', function (e) {
        var dot = e.target.closest('[data-cpg-color-sw]');
        if (dot) self._setColorName(dot.closest('.ikh-cpg__colors'), dot.dataset.colorVal);
      });
      document.addEventListener('mouseout', function (e) {
        var dot = e.target.closest('[data-cpg-color-sw]');
        if (!dot) return;
        if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('[data-cpg-color-sw]')) return;
        var wrap = dot.closest('.ikh-cpg__colors');
        var active = wrap && wrap.querySelector('[data-cpg-color-sw].ikh--active');
        self._setColorName(wrap, active ? active.dataset.colorVal : '');
      });
      this.prepare(document);
    },

    /* Pre-select the first colour on each card and warm the swatch images.
       Safe to call again for cards added later (infinite scroll, API loads). */
    prepare: function (root) {
      qsa('[data-cpg-card]', root).forEach(function (card) {
        var first = card.querySelector('[data-cpg-color-sw].ikh--active');
        if (first && !card.dataset.selectedColor) card.dataset.selectedColor = first.dataset.colorVal || '';
      });
      var warm = function () {
        qsa('[data-cpg-color-sw][data-img]', root).forEach(function (sw) {
          if (sw.dataset.img) { var i = new Image(); i.src = sw.dataset.img; }
        });
      };
      if (typeof requestIdleCallback === 'function') requestIdleCallback(warm, { timeout: 2000 });
      else setTimeout(warm, 0);
    },

    /* ── helpers ── */
    _variants: function (card) {
      var add = card.querySelector('[data-cpg-picker-add]');
      try { return JSON.parse((add && add.dataset.variants) || '[]'); } catch (e) { return []; }
    },
    _optCount: function (card) {
      var add = card.querySelector('[data-cpg-picker-add]');
      return (add && parseInt(add.dataset.optCount, 10)) || 1;
    },
    _selection: function (card) {
      var sel = {};
      qsa('.ikh-cpg__picker-swatch.ikh--active', card).forEach(function (s) {
        sel[parseInt(s.dataset.optIdx, 10)] = s.dataset.optVal;
      });
      return sel;
    },
    _match: function (variants, optCount, sel) {
      return variants.filter(function (v) {
        for (var i = 0; i < optCount; i++) {
          if (sel[i] !== undefined && v.options[i] !== sel[i]) return false;
        }
        return true;
      });
    },
    _setColorName: function (wrap, text) {
      var lbl = wrap && wrap.querySelector('[data-cpg-color-name]');
      if (lbl) lbl.textContent = text || '';
    },
    _setLabel: function (btn, text) {
      var lbl = btn.querySelector('.ikh-cpg__atc-lbl, .ikh-cpg__picker-add-lbl');
      if (lbl) lbl.textContent = text;
    },

    _renderPrice: function (card, variant) {
      var el = card.querySelector('.ikh-pc__price');
      if (!el) return;
      if (el.dataset.defaultHtml === undefined) el.dataset.defaultHtml = el.innerHTML;
      if (!variant) {
        el.innerHTML = el.dataset.defaultHtml;
        el.classList.toggle('ikh-pc__price--sale', el.dataset.defaultHtml.indexOf('<s>') > -1);
        return;
      }
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.classList.toggle('ikh-pc__price--sale', !!onSale);
      el.innerHTML = money(variant.price) + (onSale ? ' <s>' + money(variant.compare_at_price) + '</s>' : '');
    },

    _swapImage: function (card, src) {
      if (!src) return;
      var wrap = card.querySelector('.ikh-pc__img-wrap');
      var main = wrap && wrap.querySelector('img:not(.ikh-pc__img-alt)');
      var alt  = wrap && wrap.querySelector('.ikh-pc__img-alt');
      if (alt) { alt.style.cssText = 'opacity:0!important;transition:none!important;'; alt.src = src; }
      if (main && main.getAttribute('src') !== src) { main.src = src; main.removeAttribute('srcset'); }
      if (alt) requestAnimationFrame(function () { requestAnimationFrame(function () { alt.style.cssText = ''; }); });
    },

    /* ── picker state ── */
    refresh: function (card) {
      var add = card.querySelector('[data-cpg-picker-add]');
      if (!add) return;
      var variants = this._variants(card), optCount = this._optCount(card), sel = this._selection(card);

      qsa('.ikh-cpg__picker-swatch', card).forEach(function (sw) {
        var test = Object.assign({}, sel);
        test[parseInt(sw.dataset.optIdx, 10)] = sw.dataset.optVal;
        sw.classList.toggle('ikh--unavail', CardPicker._match(variants, optCount, test).length === 0);
      });

      var complete = Object.keys(sel).length >= optCount;
      var variant = complete ? this._match(variants, optCount, sel)[0] : null;
      add.classList.remove('ikh-loading', 'ikh-done');
      add.dataset.selectedVariant = variant && variant.available ? String(variant.id) : '';
      if (!complete)               { add.disabled = true;  this._setLabel(add, 'Select options'); }
      else if (!variant)           { add.disabled = true;  this._setLabel(add, 'Unavailable'); }
      else if (!variant.available) { add.disabled = true;  this._setLabel(add, 'Sold out'); }
      else                         { add.disabled = false; this._setLabel(add, 'Add to Cart'); }
      this._renderPrice(card, variant);
      if (variant && variant.featured_image) this._swapImage(card, variant.featured_image.src);
    },

    open: function (card) {
      this.closeAll(card);
      var wrap = card.querySelector('.ikh-pc__img-wrap');
      if (!wrap || !card.querySelector('[data-cpg-picker]')) return;
      qsa('.ikh-cpg__picker-swatch', card).forEach(function (s) { s.classList.remove('ikh--active'); });

      /* Colour already chosen from the dots: pre-select it and hide that row */
      var colorIdx = parseInt(card.dataset.colorOptIdx || '-1', 10);
      var preColor = card.dataset.selectedColor;
      qsa('.ikh-cpg__picker-group', card).forEach(function (grp, gi) {
        var hide = preColor && gi === colorIdx;
        grp.style.display = hide ? 'none' : '';
        if (hide) qsa('.ikh-cpg__picker-swatch', grp).forEach(function (s) {
          s.classList.toggle('ikh--active', s.dataset.optVal === preColor);
        });
      });
      wrap.classList.add('ikh--picking');
      this.refresh(card);
    },

    close: function (card) {
      var wrap = card.querySelector('.ikh-pc__img-wrap');
      if (wrap) wrap.classList.remove('ikh--picking');
      qsa('.ikh-cpg__picker-group', card).forEach(function (grp) { grp.style.display = ''; });
      this._renderPrice(card, null);
    },

    closeAll: function (except) {
      var self = this;
      qsa('.ikh-pc__img-wrap.ikh--picking').forEach(function (w) {
        var card = w.closest('[data-cpg-card]');
        if (card && card !== except) self.close(card);
      });
    },

    add: function (card, variantId, btn) {
      var self = this;
      if (!variantId || btn.classList.contains('ikh-loading')) return;
      btn.classList.add('ikh-loading'); btn.disabled = true;
      CartAPI.add(variantId, 1).then(function () {
        btn.classList.remove('ikh-loading'); btn.classList.add('ikh-done');
        self._setLabel(btn, '✓ Added');
        CartDrawer.open();
        setTimeout(function () {
          btn.classList.remove('ikh-done'); btn.disabled = false;
          self._setLabel(btn, 'Add to Cart');
          self.close(card);
        }, 1400);
      }).catch(function () {
        btn.classList.remove('ikh-loading'); btn.disabled = false;
        self._setLabel(btn, 'Error - retry');
      });
    },

    _onClick: function (e) {
      var card = e.target.closest('[data-cpg-card]');
      if (!card) { if (!e.target.closest('.ikh-pc__img-wrap')) this.closeAll(); return; }

      var dot = e.target.closest('[data-cpg-color-sw]');
      if (dot) {
        e.preventDefault(); e.stopPropagation();
        qsa('[data-cpg-color-sw]', card).forEach(function (d) { d.classList.remove('ikh--active'); });
        dot.classList.add('ikh--active');
        card.dataset.selectedColor = dot.dataset.colorVal || '';
        this._setColorName(dot.closest('.ikh-cpg__colors'), dot.dataset.colorVal);
        this._swapImage(card, dot.dataset.img);
        return;
      }

      if (e.target.closest('[data-cpg-picker-close]')) {
        e.preventDefault(); e.stopPropagation();
        this.close(card);
        return;
      }

      var atc = e.target.closest('[data-cpg-atc]');
      if (atc) {
        e.preventDefault(); e.stopPropagation();
        if (atc.disabled) return;
        if (atc.dataset.hasOptions !== 'true') { this.add(card, atc.dataset.variantId, atc); return; }
        /* Colour-only product with a colour already picked: add straight away */
        var colorIdx = parseInt(card.dataset.colorOptIdx || '-1', 10);
        if (card.dataset.selectedColor && colorIdx >= 0 && this._optCount(card) === 1) {
          var sel = {}; sel[colorIdx] = card.dataset.selectedColor;
          var v = this._match(this._variants(card), 1, sel).filter(function (x) { return x.available; })[0];
          if (v) { this.add(card, v.id, atc); return; }
        }
        this.open(card);
        return;
      }

      var sw = e.target.closest('.ikh-cpg__picker-swatch');
      if (sw) {
        e.preventDefault(); e.stopPropagation();
        if (sw.classList.contains('ikh--unavail')) return;
        qsa('.ikh-cpg__picker-swatch', sw.closest('.ikh-cpg__picker-group')).forEach(function (s) { s.classList.remove('ikh--active'); });
        sw.classList.add('ikh--active');
        this.refresh(card);
        return;
      }

      var pickerAdd = e.target.closest('[data-cpg-picker-add]');
      if (pickerAdd) {
        e.preventDefault(); e.stopPropagation();
        if (!pickerAdd.disabled) this.add(card, pickerAdd.dataset.selectedVariant, pickerAdd);
        return;
      }

      /* Any other click inside an open picker must not follow the card link */
      if (e.target.closest('[data-cpg-picker]')) { e.preventDefault(); e.stopPropagation(); }
    }
  };

  /* ── Header offset sync ───────────────────────────────── */
  var HeaderOffsets = {
    ann: null,
    hdr: null,
    init: function () {
      this.ann = document.querySelector('.ikh-ann');
      this.hdr = document.getElementById('ikh-hdr');
      if (!this.ann) return;
      /* Sync immediately, then again once fonts/layout settle */
      this.sync();
      setTimeout(this.sync.bind(this), 100);
      setTimeout(this.sync.bind(this), 500);
      /* Re-sync on resize (orientation change, text reflow) */
      window.addEventListener('resize', debounce(this.sync.bind(this), 80), { passive: true });
      /* Observe actual size changes of the announcement bar */
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(debounce(this.sync.bind(this), 40)).observe(this.ann);
      }
    },
    sync: function () {
      var ann = this.ann;
      var hdr = this.hdr;
      if (!ann) return;
      var annH = ann.getBoundingClientRect().height || ann.offsetHeight;
      var hdrH = hdr ? (hdr.getBoundingClientRect().height || hdr.offsetHeight) : (window.innerWidth <= 900 ? 56 : 68);
      /* Round up to avoid sub-pixel gaps */
      annH = Math.ceil(annH);
      hdrH = Math.ceil(hdrH);
      var offset = annH + hdrH;
      /* Update CSS variables - every element using var(--ikh-offset) updates instantly */
      var root = document.documentElement;
      root.style.setProperty('--ikh-announce-h', annH + 'px');
      root.style.setProperty('--ikh-header-h',   hdrH + 'px');
      root.style.setProperty('--ikh-offset',      offset + 'px');
      /* Explicitly position header below announcement bar */
      if (hdr) hdr.style.top = annH + 'px';
      /* Push main content so hero image starts exactly at the header's bottom edge */
      var main = document.getElementById('MainContent') ||
                 document.querySelector('main[role="main"]') ||
                 document.querySelector('.ikh-main');
      if (main) {
        main.style.setProperty('padding-top', offset + 'px', 'important');
      }
      /* Reposition search overlay and mobile nav */
      var overlay = document.querySelector('.ikh-search-overlay');
      if (overlay) overlay.style.top = offset + 'px';
      var mobileNav = document.getElementById('ikh-mobile-nav');
      if (mobileNav) mobileNav.style.top = offset + 'px';
    }
  };

  /* ── Init ─────────────────────────────────────────────── */
  function init() {
    CartDrawer.init();
    AddToCart.init();
    Search.init();
    ScrollFX.init();
    SATC.init();
    CardPicker.init();
    HeaderOffsets.init();

    /* Expose globally so other section scripts can open the drawer */
    window.InkhausCart = {
      open:    function ()       { CartDrawer.open(); },
      close:   function ()       { CartDrawer.close(); },
      refresh: function ()       { CartAPI.get().then(function (c) { updateCartBadge(c.item_count); }); },
      add:     function (id, qty) { return CartAPI.add(id, qty); },
      /* Wire up product cards added after page load (infinite scroll, API loads) */
      prepareCards: function (root) { CardPicker.prepare(root || document); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

